import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { getNews, NewsItem } from '../utils/storage';
import {
  Heart, Users, Award, Clock, Phone, Calendar,
  ArrowRight, X
} from 'lucide-react';

const slideImages = [
  // '/WEBSITE/1V8A1911.jpg',
  '/WEBSITE/Events & Activities/DSC_3084.jpg',
  '/WEBSITE/Events & Activities/DSC_2910.jpg',
  '/WEBSITE/Events & Activities/DSC_2879.jpg',
  '/WEBSITE/THUR 02C.jpg',
  '/WEBSITE/TUE 03.jpg',
  '/WEBSITE/1V8A2235.jpg',
  '/WEBSITE/1V8A2708.jpg'
];


const Home: React.FC = () => {
  const { t } = useLanguage();
  const [, setNews] = useState<NewsItem[]>([]);
  const [, setNewsLoading] = useState(true);
  const [, setNewsError] = useState<string | null>(null);
  const [showNewsModal, setShowNewsModal] = useState(false);
  const [selectedNews, setSelectedNews] = useState<NewsItem | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  const loadLatestNews = async () => {
    try {
      setNewsLoading(true);
      setNewsError(null);
      const allNews = await getNews();
      const publishedNews = allNews.filter(item => item.status === 'published').slice(0, 3);
      setNews(publishedNews);
    } catch (error) {
      console.error('Error loading news:', error);
      setNewsError('Failed to load news');
      setNews([]);
    } finally {
      setNewsLoading(false);
    }
  };


  useEffect(() => {
    loadLatestNews();
  }, []);

  useEffect(() => {
    const slideInterval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % slideImages.length);
    }, 5000);
    return () => clearInterval(slideInterval);
  }, []);


  const closeNewsModal = () => {
    setShowNewsModal(false);
    setSelectedNews(null);
  };

  const stats = [
    { icon: Users, number: '10,000+', label: t('home.patientsServed') },
    { icon: Heart, number: '50+', label: t('home.specialists') },
    { icon: Award, number: '15+', label: t('home.yearsExcellence') },
    { icon: Clock, number: '24/7', label: t('home.emergencyCare') }
  ];

  const services = [
    { title: t('home.emergencyCare'), description: '24/7 emergency medical services with rapid response team', icon: '🚨' },
    { title: 'Specialized Surgery', description: 'Advanced surgical procedures with experienced surgeons', icon: '⚕️' },
    { title: 'Maternity Care', description: 'Comprehensive care for mothers and newborns', icon: '👶' },
    { title: 'Diagnostic Imaging', description: 'Modern imaging technology for accurate diagnosis', icon: '🔬' }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative min-h-[400px] md:h-[500px] lg:h-[600px] overflow-hidden bg-gradient-to-r from-blue-800 to-[#00afad]">
        <div className="container-max section-padding h-full flex items-center relative z-10">
          {/* Left: Text with gradient background */}
          <div
            className="w-full lg:w-1/2 max-w-2xl text-white p-4 md:p-6 lg:p-8 rounded-lg bg-black bg-opacity-30 backdrop-blur-sm relative z-20"
          >
            <h1 className="text-2xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 md:mb-6 leading-tight">
              {t('home.title')}
            </h1>
            <p className="text-base md:text-lg lg:text-xl mb-6 md:mb-8 text-blue-100">
              {t('home.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
              <Link
                to="/appointment"
                className="inline-flex items-center justify-center px-4 md:px-6 py-2 md:py-3 rounded-lg font-medium bg-white text-blue-600 hover:bg-gray-100 transition-colors text-sm md:text-base"
              >
                {t('home.bookAppointment')}
                <ArrowRight className="ml-2 h-4 w-4 md:h-5 md:w-5" />
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center justify-center px-4 md:px-6 py-2 md:py-3 rounded-lg font-medium border-2 border-white text-white hover:bg-white hover:text-blue-600 transition-colors text-sm md:text-base"
              >
                {t('home.ourServices')}
              </Link>
            </div>
            
            {/* Social Media Follow Section */}
            <div className="mt-6 md:mt-8">
              <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                <span className="text-white font-medium text-sm md:text-base">Follow us</span>
                <div className="flex space-x-2 md:space-x-3">
                  <a
                    href="https://facebook.com/rabininsiahospital"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white/20 backdrop-blur-sm p-1.5 md:p-2 rounded-full hover:bg-white/30 transition-all duration-300 hover:scale-110"
                  >
                    <img src="/icons/facebook.png" alt="Facebook" className="h-4 w-4 md:h-5 md:w-5" />
                  </a>
                  <a
                    href="https://www.instagram.com/rabininsiamemorial/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white/20 backdrop-blur-sm p-1.5 md:p-2 rounded-full hover:bg-white/30 transition-all duration-300 hover:scale-110"
                  >
                    <img src="/icons/instagram.png" alt="Instagram" className="h-4 w-4 md:h-5 md:w-5" />
                  </a>
                  <a
                    href="https://twitter.com/rabininsiahospital"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white/20 backdrop-blur-sm p-1.5 md:p-2 rounded-full hover:bg-white/30 transition-all duration-300 hover:scale-110"
                  >
                    <img src="/icons/twitter.png" alt="Twitter" className="h-4 w-4 md:h-5 md:w-5" />
                  </a>
                  <a
                    href="https://tiktok.com/@rabininsiahospital"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white/20 backdrop-blur-sm p-1.5 md:p-2 rounded-full hover:bg-white/30 transition-all duration-300 hover:scale-110"
                  >
                    <img src="/icons/tiktok.png" alt="TikTok" className="h-4 w-4 md:h-5 md:w-5" />
                  </a>
                  <a
                    href="https://youtube.com/@rabininsiahospital"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white/20 backdrop-blur-sm p-1.5 md:p-2 rounded-full hover:bg-white/30 transition-all duration-300 hover:scale-110"
                  >
                    <img src="/icons/youtube.png" alt="YouTube" className="h-4 w-4 md:h-5 md:w-5" />
                  </a>
                </div>
              </div>
            </div>
          </div>
          {/* Sliding Images - Now visible on all devices */}
          <div className="absolute inset-0 lg:relative lg:w-1/2 h-full overflow-hidden lg:rounded-lg lg:ml-4 xl:ml-8">
            {slideImages.map((image, index) => (
              <img
                key={index}
                src={image}
                alt={`Hospital slide ${index + 1}`}
                className={`absolute top-0 left-0 w-full h-full object-cover transition-opacity duration-1000 lg:rounded-lg ${
                  index === currentSlide ? 'opacity-100' : 'opacity-0'
                }`}
              />
            ))}
            {/* Overlay for mobile to ensure text readability */}
            <div className="absolute inset-0 bg-black bg-opacity-40 lg:hidden"></div>
          </div>
        </div>
        {/* Slide indicators */}
        <div className="absolute bottom-4 md:bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-2 z-30">
          {slideImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 md:w-3 md:h-3 rounded-full transition-colors border border-white/50 ${
                index === currentSlide ? 'bg-white' : 'bg-white/50'
              }`}
            />
          ))}
        </div>
      </section>
      {/* Stats Section */}
      <section className="bg-gray-50 section-padding">
        <div className="container-max">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 lg:gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className="text-center p-2">
                  <div className="bg-blue-100 w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                    <Icon className="h-6 w-6 md:h-8 md:w-8 text-blue-600" />
                  </div>
                  <div className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 mb-1 md:mb-2">{stat.number}</div>
                  <div className="text-sm md:text-base text-gray-600">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      {/* News Highlights */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
  {[
    {
      image: '/WEBSITE/1V8A2109.jpg',
      date: "January 15, 2025",
      category: "Eye Care",
      title: "Specialized Eye Clinic",
      description: "Our eye specialists now provide advanced treatment for vision correction and eye diseases.",
    },
    {
      image: '/WEBSITE/WED 02A.jpg',
      date: "January 16, 2025",
      category: "Dental",
      title: "Modern Dental Clinic",
      description: "We’ve upgraded our dental department with digital X-rays and painless treatment options.",
    },
    {
      image: '/WEBSITE/Medical Equipment/dia.avif',
      date: "January 17, 2025",
      category: "Dialysis",
      title: "Advanced Dialysis Machines",
      description: "Patients with kidney conditions can now access safe and reliable dialysis treatments.",
    },
    {
      image: '/WEBSITE/Staff & Team/staff-1.JPG',
      date: "January 18, 2025",
      category: "Reception",
      title: "Smart Reception",
      description: "Our new reception system ensures faster patient registration and reduced waiting time.",
    },
    {
      image: '/WEBSITE/Patient Care/neonatal.jpeg',
      date: "January 19, 2025",
      category: "Neonatal",
      title: "Neonatal Intensive Care ",
      description: "We provide specialized care for premature and critically ill newborns in our NICU.",
    },
    {
      image: '/WEBSITE/WED 04A.jpg',
      date: "January 20, 2025",
      category: "Laboratory",
      title: "24/7 Laboratory Services",
      description: "Our lab operates round-the-clock with modern equipment for accurate diagnostics.",
    },
    {
      image: '/WEBSITE/THUR 02C.jpg',
      date: "January 21, 2025",
      category: "Surgery",
      title: "Fully Equipped Surgery Theatre",
      description: "We’ve expanded our surgical theatre with modern facilities for complex procedures.",
    },
    {
      image: '/WEBSITE/1V8A2284.JPG',
      date: "January 22, 2021",
      category: "Pharmacy",
      title: "Hospital Pharmacy Expansion",
      description: "Our pharmacy now stocks a wider range of essential and specialty medications.",
    },
    {
      image: '/WEBSITE/1V8A2674.JPG',
      date: "January 23, 2021",
      category: "Radiology",
      title: "Digital Radiology Services",
      description: "Our radiology department now offers advanced imaging like MRI, CT scans, and ultrasound.",
    },
  ].map((item, index) => (
    <div key={index} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
      <div className="h-40 md:h-48 overflow-hidden">
        <img
          src={item.image}
          alt={item.title}
          className="w-full h-full object-cover"
        />
      </div>
      <div className="p-4 md:p-6">
        <div className="flex items-center mb-3">
          <Calendar className="h-4 w-4 md:h-5 md:w-5 text-blue-600 mr-2" />
          <span className="text-sm text-gray-500">{item.date}</span>
          <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
            {item.category}
          </span>
        </div>
        <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
        <p className="text-sm md:text-base text-gray-600 mb-4">{item.description}</p>
      </div>
    </div>
  ))}
</div>

      {/* <section className="section-padding">
        <div className="container-max">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 md:mb-4">{t('home.latestNews')}</h2>
            <p className="text-lg md:text-xl text-gray-600 px-4">{t('home.stayInformed')}</p>
          </div>
          {newsLoading ? (
            <div className="text-center py-8 md:py-12">
              <div className="animate-spin rounded-full h-8 w-8 md:h-12 md:w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading latest news...</p>
            </div>
          ) : newsError ? (
            <div className="text-center py-8 md:py-12">
              <div className="text-red-600 mb-4">⚠️</div>
              <p className="text-red-600 mb-4">{newsError}</p>
              <button
                onClick={loadLatestNews}
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                Try Again
              </button>
            </div>
          ) : news.length === 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {defaultNewsImages.map((imageSrc, index) => (
                <div key={index} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                  <div className="h-40 md:h-48 overflow-hidden">
                    <img
                      src={imageSrc}
                      alt={`Hospital update ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-4 md:p-6">
                    <div className="flex items-center mb-3">
                      <Calendar className="h-4 w-4 md:h-5 md:w-5 text-blue-600 mr-2" />
                      <span className="text-sm text-gray-500">
                        {new Date().toLocaleDateString()}
                      </span>
                      <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                        Hospital Update
                      </span>
                    </div>
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3">Latest Hospital Updates </h3>
                    <p className="text-sm md:text-base text-gray-600 mb-4">Stay informed about our latest facilities, services, and healthcare improvements at Rabininsia Memorial Hospital.</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {news.map((newsItem, index) => (
                <div key={newsItem.id} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                  <div className="h-40 md:h-48 overflow-hidden">
                    <img
                      src={newsItem.imageUrl || defaultNewsImages[index % defaultNewsImages.length]}
                      alt={newsItem.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = defaultNewsImages[index % defaultNewsImages.length];
                      }}
                    />
                  </div>
                  <div className="p-4 md:p-6">
                    <div className="flex items-center mb-3">
                      <Calendar className="h-4 w-4 md:h-5 md:w-5 text-blue-600 mr-2" />
                      <span className="text-sm text-gray-500">
                        {new Date(newsItem.date).toLocaleDateString()}
                      </span>
                      <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                        {newsItem.category}
                      </span>
                    </div>
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3">{newsItem.title}</h3>
                    <p className="text-sm md:text-base text-gray-600 mb-4 line-clamp-3">{newsItem.content.substring(0, 150)}...</p>
                    <button
                      onClick={() => openNewsModal(newsItem)}
                      className="text-blue-600 hover:text-blue-800 font-medium text-sm md:text-base flex items-center"
                    >
                      Read More
                      <ArrowRight className="ml-1 h-3 w-3 md:h-4 md:w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          
        </div>
      </section> */}
      {/* Services Preview */}
      <section className="bg-gray-50 section-padding">
        <div className="container-max">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 md:mb-4">{t('home.keyServices')}</h2>
            <p className="text-lg md:text-xl text-gray-600 px-4">{t('home.comprehensiveHealthcare')}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg p-4 md:p-6 text-center hover:shadow-xl transition-shadow">
                <div className="text-3xl md:text-4xl mb-3 md:mb-4">{service.icon}</div>
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 md:mb-3">{service.title}</h3>
                <p className="text-sm md:text-base text-gray-600">{service.description}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-8 md:mt-12">
            <Link
              to="/services"
              className="inline-flex items-center px-4 md:px-6 py-2 md:py-3 rounded-lg font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors text-sm md:text-base"
            >
              {t('home.viewAllServices')}
              <ArrowRight className="ml-2 h-4 w-4 md:h-5 md:w-5" />
            </Link>
          </div>
        </div>
      </section>
      {/* Emergency Contact */}
      <section className="bg-red-600 text-white section-padding">
        <div className="container-max">
          <div className="text-center">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-3 md:mb-4">{t('home.medicalEmergency')}</h2>
            <p className="text-lg md:text-xl mb-6 md:mb-8 text-red-100 px-4">{t('home.emergencyDept')}</p>
            <div className="flex flex-col lg:flex-row items-center justify-center gap-6 md:gap-8">
              <div className="flex items-center space-x-3">
                <Phone className="h-6 w-6 md:h-8 md:w-8" />
                <div>
                  <div className="text-xl md:text-2xl font-bold">+255 658 849 670</div>
                  <div className="text-sm md:text-base text-red-100">{t('home.emergencyHotline')}</div>
                </div>
              </div>
              <div className="hidden lg:block w-px h-16 bg-red-400"></div>
              <div className="flex items-center space-x-3">
                <Clock className="h-6 w-6 md:h-8 md:w-8" />
                <div>
                  <div className="text-xl md:text-2xl font-bold">24/7</div>
                  <div className="text-sm md:text-base text-red-100">{t('home.alwaysAvailable')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="section-padding">
        <div className="container-max">
          <div className="bg-blue-600 rounded-2xl text-white p-6 md:p-8 lg:p-12 text-center">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-3 md:mb-4">{t('home.readyToSchedule')}</h2>
            <p className="text-lg md:text-xl mb-6 md:mb-8 text-blue-100 px-4">{t('home.bookWithProfessionals')}</p>
            <Link
              to="/appointment"
              className="inline-flex items-center px-4 md:px-6 py-2 md:py-3 rounded-lg font-medium bg-white text-blue-600 hover:bg-gray-100 transition-colors text-sm md:text-base"
            >
              {t('home.bookNow')}
              <ArrowRight className="ml-2 h-4 w-4 md:h-5 md:w-5" />
            </Link>
          </div>
        </div>
      </section>
      {/* News Modal */}
      {showNewsModal && selectedNews && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 md:p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[95vh] md:max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full flex-shrink-0">
                  {selectedNews.category}
                </span>
                <span className="text-xs md:text-sm text-gray-500">
                  {new Date(selectedNews.date).toLocaleDateString()}
                </span>
              </div>
              <button
                onClick={closeNewsModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            {/* Content */}
            <div className="p-4 md:p-6">
              {selectedNews.imageUrl && (
                <div className="mb-4 md:mb-6">
                  <img
                    src={selectedNews.imageUrl}
                    alt={selectedNews.title}
                    className="w-full h-48 md:h-64 object-cover rounded-lg"
                  />
                </div>
              )}
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 md:mb-4">{selectedNews.title}</h2>
              <div className="text-xs md:text-sm text-gray-500 mb-3 md:mb-4">
                By {selectedNews.author} • {new Date(selectedNews.date).toLocaleDateString()}
              </div>
              <div className="prose max-w-none">
                <p className="text-sm md:text-base text-gray-700 leading-relaxed whitespace-pre-line">
                  {selectedNews.content}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;